// src/components/employee/EmployeeAttendance.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Clock,
    Calendar,
    CheckCircle,
    XCircle,
    AlertCircle,
    UserPlus,
    Lock,
    MapPin,
    Timer,
    TrendingUp,
    Coffee,
    Briefcase,
    ChevronLeft,
    ChevronRight,
    Info,
    LogIn,
    LogOut,
    Activity,
    Users,
    Award,
    PauseCircle,
    Home,
    Zap,
    AlertTriangle,
    Check,
    X,
    Filter,
    Eye
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useEmployees } from '../../contexts/EmployeeContext';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useAttendance } from '../../contexts/AttendanceContext';
import { useLeave } from '../../contexts/LeaveContext';
import attendanceService from '../../services/attendance.service';
import leaveService from '../../services/leave.service';
import holidayPlanService from '../../services/holidayPlan.service';
import employeeService from '../../services/employee.service';
import conflictResolutionService from '../../services/conflictResolution.service';
import AttendanceRegularizationModal from '../common/AttendanceRegularizationModal';
import LeaveRequestModal from '../common/LeaveRequestModal';
import WorkFromHomeModal from '../common/WorkFromHomeModal';
import {
    toISTDateString,
    getISTDateFromUTC,
    formatISTDate,
    isSameISTDay,
    getCurrentISTDate,
    getISTComponents,
    parseISTDate,
    formatTime,
    formatDate
} from '../../utils/dateHelpers';

// Helper function to format date in IST timezone (YYYY-MM-DD)
const formatLocalDate = (date = new Date()) => {
    return toISTDateString(date);
};

// Helper function to extract IST date from backend timestamp string
// Backend returns dates like "2025-01-15T18:30:00.000Z" (UTC)
// We need to convert to IST date "2025-01-16"
const getLocalDateFromTimestamp = (timestamp) => {
    return getISTDateFromUTC(timestamp);
};

// Normalize timestamps coming from the API (handles missing timezone offsets)
const parseApiTimestamp = (timestamp) => {
    if (!timestamp) return null;
    if (timestamp instanceof Date) {
        return Number.isNaN(timestamp.getTime()) ? null : timestamp;
    }

    const trimmed = String(timestamp).trim();
    if (!trimmed) return null;

    // Numeric timestamps (milliseconds or seconds)
    if (/^\d+$/.test(trimmed)) {
        const numericValue = Number(trimmed);
        if (!Number.isNaN(numericValue)) {
            const parsed = new Date(trimmed.length === 10 ? numericValue * 1000 : numericValue);
            return Number.isNaN(parsed.getTime()) ? null : parsed;
        }
    }

    // Strings with explicit timezone information
    if (/([Zz]|[+-]\d{2}:?\d{2})$/.test(trimmed)) {
        const parsed = new Date(trimmed);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    // Handle common "YYYY-MM-DD HH:mm:ss(.SSS)" formats as IST time (not browser local)
    const normalized = trimmed.replace('T', ' ');
    const localMatch = normalized.match(
        /^(\d{4})-(\d{2})-(\d{2})(?:\s+(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,6}))?)?)?$/
    );

    if (localMatch) {
        const [, year, month, day, hour = '0', minute = '0', second = '0', fractional = '0'] = localMatch;
        const milliseconds = fractional
            ? Math.round(parseFloat(`0.${fractional}`) * 1000)
            : 0;

        // FIXED: Interpret timestamps without timezone as IST (+05:30)
        // Backend stores times in IST, so we need to parse them as IST, not browser local
        const pad = (n) => String(n).padStart(2, '0');
        const isoWithIST = `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}:${pad(second)}.${String(milliseconds).padStart(3, '0')}+05:30`;
        const parsed = new Date(isoWithIST);

        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    // Fallback to native parsing
    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const EmployeeAttendance = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { employees } = useEmployees();
    const { onboardingData, getCompletionPercentage } = useOnboarding();
    const { syncAttendanceFromResponse } = useAttendance();
    const { rawLeaveBalance, requestLeave, fetchLeaveData } = useLeave();

    // State Management
    const [loading, setLoading] = useState(false);
    const [attendanceData, setAttendanceData] = useState(null);
    const [leaveBalance, setLeaveBalance] = useState(null);
    const [holidays, setHolidays] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [checkInTime, setCheckInTime] = useState(null);
    const [checkOutTime, setCheckOutTime] = useState(null);
    const [activeTimer, setActiveTimer] = useState(0);
    const [isTimerActive, setIsTimerActive] = useState(false);
    const [calendarView, setCalendarView] = useState('month');
    const [employeeDetails, setEmployeeDetails] = useState(null);

    // NEW: Multiple punch and regularization state
    const [todayPunches, setTodayPunches] = useState([]);
    const [canCheckIn, setCanCheckIn] = useState(true);
    const [canCheckOut, setCanCheckOut] = useState(false);
    const [showRegularizationModal, setShowRegularizationModal] = useState(false);
    const [regularizationRequests, setRegularizationRequests] = useState([]);

    // Enhanced calendar state
    const [selectedDayDetails, setSelectedDayDetails] = useState(null);
    const [showDayModal, setShowDayModal] = useState(false);
    const [calendarFilter, setCalendarFilter] = useState('all'); // all, present, absent, leaves, wfh
    const [showConflictModal, setShowConflictModal] = useState(false);
    const [selectedConflict, setSelectedConflict] = useState(null);
    const [conflictResolutionLoading, setConflictResolutionLoading] = useState(false);
    const [hoveredDate, setHoveredDate] = useState(null);

    // NEW: Modal state for absent day actions
    const [showLeaveModalFromAbsent, setShowLeaveModalFromAbsent] = useState(false);
    const [showWFHModalFromAbsent, setShowWFHModalFromAbsent] = useState(false);
    const [showRegularizationModalFromAbsent, setShowRegularizationModalFromAbsent] = useState(false);
    const [selectedAbsentDate, setSelectedAbsentDate] = useState(null);

    // NEW: Modal state for OT/Comp-off requests on weekends/holidays
    const [showOTCompOffModal, setShowOTCompOffModal] = useState(false);
    const [selectedOTDate, setSelectedOTDate] = useState(null);

    const timerRef = useRef(null);
    const currentEmployee = employees.find(emp => emp.email === user?.email);

    // Check if onboarded
    const isOnboarded = user?.id && onboardingData ?
        onboardingData[user.id]?.adminReviewed === true : false;
    const completionPercentage = getCompletionPercentage(currentEmployee?.id);

    // Fetch employee details with holiday plan
    const fetchEmployeeDetails = useCallback(async () => {
        if (!currentEmployee?.id) return;

        try {
            console.log('📋 Fetching employee details for ID:', currentEmployee.id);
            const response = await employeeService.getEmployeeById(currentEmployee.id);
            console.log('📋 Employee response:', response);

            if (response.success) {
                setEmployeeDetails(response.employee);
                console.log('📋 Employee details set:', response.employee);
                console.log('📋 Holiday Plan ID:', response.employee.holiday_plan_id);
                console.log('📋 Weekly Off:', response.employee.weekly_off);

                // Fetch holidays if employee has a holiday plan
                if (response.employee.holiday_plan_id) {
                    console.log('🎉 Fetching holidays for plan:', response.employee.holiday_plan_id);
                    await fetchHolidaysForEmployee(response.employee.holiday_plan_id);
                } else {
                    console.warn('⚠️ No holiday_plan_id assigned to employee');
                }
            }
        } catch (error) {
            console.error('❌ Error fetching employee details:', error);
        }
    }, [currentEmployee?.id]);

    // Fetch holidays for the employee's holiday plan (filtered for optional holidays)
    const fetchHolidaysForEmployee = async (holidayPlanId) => {
        try {
            console.log('🎉 Calling holidayPlanService.getHolidayPlanById with ID:', holidayPlanId);
            const planDetails = await holidayPlanService.getHolidayPlanById(holidayPlanId);
            console.log('🎉 Holiday plan details received:', planDetails);

            if (planDetails && planDetails.holidays) {
                // Fetch approved optional holidays for this employee
                let approvedOptionalHolidayIds = new Set();
                try {
                    const myRequests = await holidayPlanService.getMyOptionalHolidayRequests();
                    approvedOptionalHolidayIds = new Set(
                        myRequests
                            .filter(r => r.status === 'approved')
                            .map(r => r.holiday_date_id)
                    );
                } catch (err) {
                    console.warn('Could not fetch approved optional holidays:', err);
                }

                // Filter holidays: include non-optional + approved optional holidays
                const filteredHolidays = planDetails.holidays.filter(holiday => {
                    const isOptional = holiday.is_optional || holiday.isOptional;
                    if (!isOptional) {
                        return true; // Non-optional holidays are always shown
                    }
                    // Optional holidays only shown if approved
                    return approvedOptionalHolidayIds.has(holiday.id);
                });

                console.log('🎉 Setting holidays (filtered):', filteredHolidays.length, 'of', planDetails.holidays.length);
                setHolidays(filteredHolidays);
            } else {
                console.warn('⚠️ No holidays found in plan details');
                setHolidays([]);
            }
        } catch (error) {
            console.error('❌ Error fetching holidays:', error);
            setHolidays([]);
        }
    };

    // Fetch attendance data
    const fetchAttendanceData = useCallback(async () => {
        if (!currentEmployee?.id) return;

        setLoading(true);
        try {
            // Fetch attendance data from attendance controller
            const response = await attendanceService.getMyAttendance(selectedMonth + 1, selectedYear);
            // Fetch leave data from leave controller (source of truth for leaves)
            const allLeaves = await leaveService.getLeaveHistory();
            console.log('📋 Fetched leaves from leave controller:', allLeaves.length);

            // Filter leaves for the selected month and generate daily leave records
            const leaveRecordsForMonth = [];
            const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();

            for (const leave of allLeaves) {
                // Only process approved or pending leaves
                if (!['approved', 'pending'].includes(leave.status)) continue;

                // Convert UTC timestamp to IST date
                // Backend sends "2025-10-21T18:30:00.000Z" (UTC)
                // We need to convert to IST and get the date part
                const fromDateStr = getISTDateFromUTC(leave.from_date);
                const toDateStr = getISTDateFromUTC(leave.to_date);

                console.log('🗓️ Processing leave:', {
                    id: leave.id,
                    rawFromDate: leave.from_date,
                    rawToDate: leave.to_date,
                    istFromDate: fromDateStr,
                    istToDate: toDateStr,
                    leaveType: leave.leave_type,
                    status: leave.status
                });

                const [fromYear, fromMonth, fromDay] = fromDateStr.split('-').map(Number);
                const [toYear, toMonth, toDay] = toDateStr.split('-').map(Number);

                // Create dates in IST timezone
                const fromDate = new Date(fromYear, fromMonth - 1, fromDay);
                const toDate = new Date(toYear, toMonth - 1, toDay);

                // Iterate through each day in the leave period
                let currentDate = new Date(fromDate);
                while (currentDate <= toDate) {
                    const year = currentDate.getFullYear();
                    const month = currentDate.getMonth();
                    const day = currentDate.getDate();

                    // Only include dates in the selected month/year
                    if (month === selectedMonth && year === selectedYear) {
                        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

                        // Check if this date already has an attendance record
                        // IMPORTANT: Convert attendance date from UTC to IST for proper comparison
                        const hasAttendance = response.monthly?.some(record => {
                            // Attendance dates come as UTC timestamps, convert to IST date
                            const attendanceDateIST = getISTDateFromUTC(record.date);
                            return attendanceDateIST === dateStr;
                        });

                        // Only add leave record if no attendance exists for this day
                        if (!hasAttendance) {
                            console.log(`  ✅ Adding leave record for ${dateStr}`);
                            leaveRecordsForMonth.push({
                                id: `leave_${leave.id}_${dateStr}`,
                                employee_id: currentEmployee.id,
                                date: dateStr,
                                status: 'leave',
                                leave_type: leave.leave_type || 'Leave',
                                leave_status: leave.status,
                                approval_status: leave.status,
                                check_in_time: null,
                                check_out_time: null,
                                work_hours: 0,
                                overtime_hours: 0,
                                leave_request_id: leave.id,
                                is_half_day: leave.is_half_day || false
                            });
                        } else {
                            console.log(`  ⚠️ Skipping ${dateStr} - attendance exists`);
                        }
                    }

                    currentDate.setDate(currentDate.getDate() + 1);
                }
            }

            console.log('📅 Generated leave records for month:', leaveRecordsForMonth.length);
            if (leaveRecordsForMonth.length > 0) {
                console.log('📅 Leave records:', leaveRecordsForMonth.map(r => ({ date: r.date, type: r.leave_type })));
            }

            // Check if attendance API already has leave records (shouldn't happen after backend changes)
            const existingLeaveRecords = (response.monthly || []).filter(r => r.status === 'leave');
            if (existingLeaveRecords.length > 0) {
                console.warn('⚠️ Attendance API returned leave records (should not happen):',
                    existingLeaveRecords.map(r => ({
                        date: r.date,
                        dateIST: getISTDateFromUTC(r.date),
                        status: r.status,
                        leaveType: r.leave_type
                    }))
                );
            }

            // Merge attendance records with leave records
            const mergedMonthly = [...(response.monthly || []), ...leaveRecordsForMonth].sort((a, b) => {
                return new Date(b.date) - new Date(a.date);
            });

            // Update response with merged data
            const updatedResponse = {
                ...response,
                monthly: mergedMonthly
            };

            // Recalculate stats to include frontend-generated leave records
            // Only count APPROVED leaves in statistics (not pending)
            const approvedLeaveCount = leaveRecordsForMonth.filter(l => l.approval_status === 'approved').length;
            const updatedStats = {
                ...response.stats,
                leaveDays: (response.stats?.leaveDays || 0) + approvedLeaveCount,
                absentDays: Math.max(0, (response.stats?.absentDays || 0) - approvedLeaveCount) // Reduce absent count by approved leave count
            };
            updatedResponse.stats = updatedStats;

            console.log('✅ Updated attendance stats after adding frontend leaves:', {
                totalWorkingDays: updatedStats.totalWorkingDays,
                presentDays: updatedStats.presentDays,
                leaveDays: updatedStats.leaveDays,
                absentDays: updatedStats.absentDays,
                totalLeavesGenerated: leaveRecordsForMonth.length,
                approvedLeavesAdded: approvedLeaveCount,
                averageWorkHours: updatedStats.averageWorkHours,
                totalOvertime: updatedStats.totalOvertime
            });

            setAttendanceData(updatedResponse);
            if (currentEmployee?.id) {
                syncAttendanceFromResponse(currentEmployee.id, updatedResponse);
            }

            // Set check-in/out times for today
            if (response.today) {
                setCheckInTime(response.today.check_in_time);
                setCheckOutTime(response.today.check_out_time);

                // NOTE: Timer logic is handled by fetchTodayPunches() which uses the latest check-in
                // Don't set timer here to avoid using response.today.check_in_time which may be the first check-in of the day
            }
        } catch (error) {
            console.error('Error fetching attendance:', error);
        } finally {
            setLoading(false);
        }
    }, [currentEmployee?.id, selectedMonth, selectedYear, syncAttendanceFromResponse]);

    // Fetch leave balance
    const fetchLeaveBalance = useCallback(async () => {
        if (!currentEmployee?.id) return;

        try {
            const response = await leaveService.getLeaveBalance();
            setLeaveBalance(response.balance);
        } catch (error) {
            console.error('Error fetching leave balance:', error);
        }
    }, [currentEmployee?.id]);

    // NEW: Fetch today's punch records
    const fetchTodayPunches = useCallback(async () => {
        try {
            const response = await attendanceService.getTodayPunches();
            const punchPayload = response?.data ?? response;
            const punches = punchPayload?.punches || [];
            const normalizedPunches = punches.map(punch => ({
                ...punch,
                parsedTime: parseApiTimestamp(punch.punch_time)
            }));
            const nextCanCheckIn = punchPayload?.canCheckIn ?? punchPayload?.can_check_in;
            const nextCanCheckOut = punchPayload?.canCheckOut ?? punchPayload?.can_check_out;
            const allowCheckIn = nextCanCheckIn !== undefined ? nextCanCheckIn : true;
            const allowCheckOut = nextCanCheckOut !== undefined
                ? nextCanCheckOut
                : normalizedPunches.some(punch => {
                    if (punch.punch_type !== 'check_in' || !punch.parsedTime) return false;
                    return !normalizedPunches.some(other =>
                        other.punch_type === 'check_out' &&
                        other.parsedTime &&
                        other.parsedTime > punch.parsedTime
                    );
                });

            setTodayPunches(punches);
            setCanCheckIn(allowCheckIn);
            setCanCheckOut(allowCheckOut);

            // Update timer if checked in
            if (allowCheckOut) {
                // Find ALL check-ins without a corresponding check-out, then get the LAST one
                const activeCheckIns = normalizedPunches.filter(punch => {
                    if (punch.punch_type !== 'check_in' || !punch.parsedTime) return false;
                    return !normalizedPunches.some(other =>
                        other.punch_type === 'check_out' &&
                        other.parsedTime &&
                        other.parsedTime > punch.parsedTime
                    );
                });

                // Get the most recent (last) active check-in
                const lastCheckIn = activeCheckIns.length > 0 ? activeCheckIns[activeCheckIns.length - 1] : null;

                if (lastCheckIn?.parsedTime) {
                    const now = new Date();
                    const elapsed = Math.max(0, Math.floor((now.getTime() - lastCheckIn.parsedTime.getTime()) / 1000));
                    setActiveTimer(elapsed);  // Set to calculated elapsed time, not 0
                    setIsTimerActive(true);
                }
            } else {
                setIsTimerActive(false);
                setActiveTimer(0);
            }
        } catch (error) {
            console.error('Error fetching today punches:', error);
        }
    }, []);

    // NEW: Fetch regularization requests
    const fetchRegularizationRequests = useCallback(async () => {
        try {
            const result = await attendanceService.getMyRegularizationRequests();
            if (result.success) {
                setRegularizationRequests(result.data?.requests || []);
            } else {
                console.error('Error fetching regularization requests:', result.error);
                setRegularizationRequests([]);
            }
        } catch (error) {
            console.error('Error fetching regularization requests:', error);
            setRegularizationRequests([]);
        }
    }, []);

    // Timer effect
    useEffect(() => {
        if (isTimerActive) {
            timerRef.current = setInterval(() => {
                setActiveTimer(prev => prev + 1);
            }, 1000);
        } else {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        }

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [isTimerActive]);

    // Fetch employee details first
    useEffect(() => {
        fetchEmployeeDetails();
    }, [fetchEmployeeDetails]);

    // Fetch attendance data when employee details are loaded
    useEffect(() => {
        if (employeeDetails) {
            fetchAttendanceData();
            fetchLeaveBalance();
            fetchTodayPunches();
            fetchRegularizationRequests();
        }
    }, [employeeDetails, selectedMonth, selectedYear, fetchAttendanceData, fetchLeaveBalance, fetchTodayPunches, fetchRegularizationRequests]);

    // Log app load activity silently (for anti-misuse tracking)
    useEffect(() => {
        // Fire and forget - don't wait or handle errors
        attendanceService.logActivity('app_load', {
            deviceType: 'web',
            metadata: { page: 'EmployeeAttendance' }
        });
    }, []); // Only log once when component mounts

    // ─── Geolocation permission pre-check ─────────────────────────────
    const [locationPermission, setLocationPermission] = useState('unknown');
    useEffect(() => {
        if (navigator.permissions?.query) {
            navigator.permissions.query({ name: 'geolocation' }).then(result => {
                setLocationPermission(result.state);
                result.onchange = () => setLocationPermission(result.state);
            }).catch(() => {});
        }
    }, []);

    // ─── IP-based geolocation fallback ──────────────────────────────
    const getIpLocation = async () => {
        // Try ipapi.co first
        try {
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 5000);
            const res = await fetch('https://ipapi.co/json/', { signal: controller.signal });
            clearTimeout(timeout);
            if (res.ok) {
                const data = await res.json();
                if (data.latitude && data.longitude) {
                    console.log('📍 IP location acquired (ipapi.co)');
                    return { latitude: data.latitude, longitude: data.longitude, accuracy: 5000, type: 'ip' };
                }
            }
        } catch (e) {
            console.warn('⚠️ ipapi.co failed:', e.message);
        }
        // Backup: ip-api.com
        try {
            const controller2 = new AbortController();
            const timeout2 = setTimeout(() => controller2.abort(), 5000);
            const res2 = await fetch('http://ip-api.com/json/?fields=status,lat,lon', { signal: controller2.signal });
            clearTimeout(timeout2);
            if (res2.ok) {
                const data2 = await res2.json();
                if (data2.status === 'success' && data2.lat && data2.lon) {
                    console.log('📍 IP location acquired (ip-api.com)');
                    return { latitude: data2.lat, longitude: data2.lon, accuracy: 5000, type: 'ip' };
                }
            }
        } catch (e2) {
            console.warn('⚠️ ip-api.com also failed:', e2.message);
        }
        return null;
    };

    // ─── Browser geolocation with IP fallback cascade ───────────────
    const getCurrentLocation = () => {
        return new Promise((resolve) => {
            if (!navigator.geolocation) {
                console.warn('⚠️ Geolocation API not supported, trying IP fallback');
                getIpLocation().then(resolve);
                return;
            }

            let resolved = false;
            const done = (result) => {
                if (resolved) return;
                resolved = true;
                resolve(result);
            };

            // Overall safety timeout — fall back to IP geolocation
            const safetyTimeout = setTimeout(() => {
                if (resolved) return;
                console.warn('⏱️ Browser location timed out after 12s, trying IP fallback');
                getIpLocation().then(done);
            }, 12000);

            const wrapDone = (result) => {
                clearTimeout(safetyTimeout);
                done(result);
            };

            // Step 1: Try high accuracy (GPS) — 8s timeout for Mac Wi-Fi positioning
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    console.log('📍 High-accuracy location acquired, accuracy:', Math.round(position.coords.accuracy), 'm');
                    wrapDone({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                        accuracy: position.coords.accuracy
                    });
                },
                (err) => {
                    // Step 2: GPS failed — try network/cached location
                    console.log('📍 GPS unavailable (' + err.message + '), trying network location...');
                    navigator.geolocation.getCurrentPosition(
                        (position) => {
                            console.log('📍 Network location acquired, accuracy:', Math.round(position.coords.accuracy), 'm');
                            wrapDone({
                                latitude: position.coords.latitude,
                                longitude: position.coords.longitude,
                                accuracy: position.coords.accuracy
                            });
                        },
                        (err2) => {
                            // Step 3: Browser geo completely failed — IP fallback
                            console.warn('⚠️ Browser location failed (' + err2.message + '), trying IP fallback');
                            clearTimeout(safetyTimeout);
                            getIpLocation().then(done);
                        },
                        { enableHighAccuracy: false, timeout: 5000, maximumAge: 300000 }
                    );
                },
                { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
            );
        });
    };

    // NEW: Handle punch (check-in or check-out)
    const handlePunch = async (punchType) => {
        if (!user?.id) {
            console.error('No user ID found');
            return;
        }

        console.log(`🔵 Starting ${punchType} process...`);
        setLoading(true);

        try {
            console.log('📍 Getting location...');
            const location = await getCurrentLocation();

            if (location) {
                console.log('📍 Location obtained:', JSON.stringify(location));
            } else {
                console.warn('📍 No location available — punch will proceed without GPS');
            }

            // Include shift_id to help backend determine correct attendance_date for overnight shifts
            const shiftId = employeeDetails?.shift_id || currentEmployee?.shift_id;
            console.log(`📡 Calling attendanceService.punch(${punchType}), shift_id: ${shiftId}...`);
            const response = await attendanceService.punch(punchType, location, null, shiftId);
            console.log('📡 API Response received:', response);

            const result = response?.data ?? response;
            console.log('📦 Extracted result:', result);

            const successFlag = result?.success ?? (result?.status ? result.status === 'success' : undefined);
            const isSuccess = successFlag !== undefined ? successFlag : !result?.error;
            const successMessage = result?.message || `${punchType === 'check_in' ? 'Checked in' : 'Checked out'} successfully!`;

            console.log(`✅ Success flag: ${isSuccess}, Message: ${successMessage}`);

            if (!isSuccess) {
                const errorMessage = result?.error || result?.message || `Failed to ${punchType === 'check_in' ? 'check in' : 'check out'}`;
                throw new Error(errorMessage);
            }

            // Show geofence/verification warning if applicable
            const geofence = result?.geofence || response?.data?.geofence;
            const ipVerification = result?.ipVerification || response?.data?.ipVerification;
            const verificationMethod = result?.verificationMethod || response?.data?.verificationMethod;

            if (punchType === 'check_in') {
                if (verificationMethod === 'wfh') {
                    alert('Working from home today. Punch recorded.');
                } else if (verificationMethod === 'ip') {
                    alert(`Location verified via office network (${ipVerification?.networkName || 'Office'}). ${successMessage}`);
                } else if (verificationMethod === 'none' && (geofence?.officesConfigured || ipVerification?.networksConfigured)) {
                    alert(`⚠️ Location could not be verified. Your punch is recorded but flagged for admin review.`);
                } else if (geofence && geofence.officesConfigured && !geofence.withinGeofence) {
                    alert(`⚠️ You appear to be outside the office area (${geofence.distanceMeters}m from ${geofence.locationName || 'nearest office'}). Your punch is recorded as 'Remote'.`);
                } else {
                    alert(successMessage);
                }
            } else {
                alert(successMessage);
            }

            console.log('🔄 Fetching updated punch data...');
            await fetchTodayPunches();
            await fetchAttendanceData();
            console.log('✅ Punch process completed successfully');
        } catch (error) {
            console.error(`❌ Punch error:`, error);
            console.error('Error details:', {
                message: error.message,
                response: error.response,
                stack: error.stack
            });

            const errorMessage =
                error.response?.data?.error ||
                error.message ||
                `Failed to ${punchType === 'check_in' ? 'check in' : 'check out'}`;
            alert(errorMessage);
        } finally {
            console.log('🏁 Setting loading to false');
            setLoading(false);
        }
    };

    // LEGACY: Handle check-in (redirects to new punch)
    const handleCheckIn = () => handlePunch('check_in');

    // LEGACY: Handle check-out (redirects to new punch)
    const handleCheckOut = () => handlePunch('check_out');

    // Format timer display
    const formatTimer = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // Get calendar days
    const getCalendarDays = () => {
        const firstDay = new Date(selectedYear, selectedMonth, 1);
        const lastDay = new Date(selectedYear, selectedMonth + 1, 0);
        const startDate = new Date(firstDay);
        startDate.setDate(startDate.getDate() - firstDay.getDay());

        const days = [];
        const current = new Date(startDate);

        while (current <= lastDay || current.getDay() !== 0) {
            days.push(new Date(current));
            current.setDate(current.getDate() + 1);
        }

        return days;
    };

    // Check if employee arrived early (before shift start time - this is good!)
    const isEarlyArrival = (attendance) => {
        if (!attendance?.check_in_time || !employeeDetails?.shift_start_time) return false;

        const checkInTime = parseApiTimestamp(attendance.check_in_time);
        if (!checkInTime) return false;

        const [shiftHour, shiftMin] = employeeDetails.shift_start_time.split(':').map(Number);

        // Use getISTComponents to get hours in IST timezone (not browser local)
        const { hour: checkInHour, minute: checkInMin } = getISTComponents(checkInTime);

        const checkInMinutes = checkInHour * 60 + checkInMin;
        const shiftMinutes = shiftHour * 60 + shiftMin;

        return checkInMinutes < shiftMinutes;
    };

    // Check if employee arrived late (after shift start time + grace period buffer)
    const isLateArrival = (attendance) => {
        if (!attendance?.check_in_time || !employeeDetails?.shift_start_time) return false;

        const checkInTime = parseApiTimestamp(attendance.check_in_time);
        if (!checkInTime) return false;

        const [shiftHour, shiftMin] = employeeDetails.shift_start_time.split(':').map(Number);
        const gracePeriod = employeeDetails?.shift_grace_period || 15; // Default 15 minutes grace period

        // Use getISTComponents to get hours in IST timezone (not browser local)
        const { hour: checkInHour, minute: checkInMin } = getISTComponents(checkInTime);

        const checkInMinutes = checkInHour * 60 + checkInMin;
        const shiftMinutes = shiftHour * 60 + shiftMin;
        const allowedLateMinutes = shiftMinutes + gracePeriod;

        return checkInMinutes > allowedLateMinutes;
    };

    // Get attendance status for a date
    const getAttendanceStatus = (date) => {
        if (!attendanceData?.monthly) return null;

        const dateStr = formatLocalDate(date);
        return attendanceData.monthly.find(record =>
            getLocalDateFromTimestamp(record.date) === dateStr
        );
    };

    // Check if date is a weekly off based on employee's weekly_off configuration
    const isWeeklyOff = (date) => {
        if (!employeeDetails?.weekly_off) return false;

        const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
        const weeklyOff = employeeDetails.weekly_off.toLowerCase();

        // Check various weekly off configurations
        if (weeklyOff.includes('saturday') && weeklyOff.includes('sunday')) {
            return dayOfWeek === 0 || dayOfWeek === 6;
        } else if (weeklyOff.includes('sunday')) {
            return dayOfWeek === 0;
        } else if (weeklyOff.includes('saturday')) {
            return dayOfWeek === 6;
        } else if (weeklyOff.includes('monday')) {
            return dayOfWeek === 1;
        } else if (weeklyOff.includes('tuesday')) {
            return dayOfWeek === 2;
        } else if (weeklyOff.includes('wednesday')) {
            return dayOfWeek === 3;
        } else if (weeklyOff.includes('thursday')) {
            return dayOfWeek === 4;
        } else if (weeklyOff.includes('friday')) {
            return dayOfWeek === 5;
        }

        return false;
    };

    // Check if date is a holiday
    const isHoliday = (date) => {
        const dateStr = formatLocalDate(date);
        return holidays.some(holiday =>
            getLocalDateFromTimestamp(holiday.date) === dateStr
        );
    };

    // Get holiday info for a date
    const getHolidayInfo = (date) => {
        const dateStr = formatLocalDate(date);
        return holidays.find(holiday =>
            getLocalDateFromTimestamp(holiday.date) === dateStr
        );
    };

    const firstCheckInPunch = todayPunches.find(p => p.punch_type === 'check_in');
    const firstCheckInMoment = parseApiTimestamp(firstCheckInPunch?.punch_time);
    const lastCheckOutPunch = (() => {
        for (let i = todayPunches.length - 1; i >= 0; i -= 1) {
            if (todayPunches[i].punch_type === 'check_out') {
                return todayPunches[i];
            }
        }
        return null;
    })();
    const lastCheckOutMoment = parseApiTimestamp(lastCheckOutPunch?.punch_time);

    // Navigate months
    const navigateMonth = (direction) => {
        if (direction === 'prev') {
            if (selectedMonth === 0) {
                setSelectedMonth(11);
                setSelectedYear(selectedYear - 1);
            } else {
                setSelectedMonth(selectedMonth - 1);
            }
        } else {
            if (selectedMonth === 11) {
                setSelectedMonth(0);
                setSelectedYear(selectedYear + 1);
            } else {
                setSelectedMonth(selectedMonth + 1);
            }
        }
    };

    // NEW: Handle absent day actions
    const handleAbsentDayLeaveRequest = (date) => {
        setSelectedAbsentDate(date);
        setShowDayModal(false);
        setShowLeaveModalFromAbsent(true);
    };

    const handleAbsentDayWFHRequest = (date) => {
        setSelectedAbsentDate(date);
        setShowDayModal(false);
        setShowWFHModalFromAbsent(true);
    };

    const handleAbsentDayRegularization = (date) => {
        setSelectedAbsentDate(date);
        setShowDayModal(false);
        setShowRegularizationModalFromAbsent(true);
    };

    // Handle leave request submission
    const handleLeaveRequestSubmit = async (formData) => {
        if (!currentEmployee?.id) return;

        try {
            console.log('🔍 Raw leave request data from modal:', formData);

            // Calculate total days
            const calculateLeaveDays = (fromDate, toDate, isHalfDay) => {
                if (!fromDate || !toDate) return 0;
                const start = new Date(fromDate);
                const end = new Date(toDate);
                if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
                    return 0;
                }
                const diffTime = end.getTime() - start.getTime();
                const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                const total = diffDays >= 0 ? diffDays + 1 : 1;
                return isHalfDay ? 0.5 : total;
            };

            const totalDays = calculateLeaveDays(formData.fromDate, formData.toDate, formData.isHalfDay);

            // Use camelCase format as expected by LeaveContext.requestLeave
            const processedData = {
                leaveTypeId: formData.leaveTypeId,
                fromDate: formData.fromDate,
                toDate: formData.toDate,
                days: totalDays,
                reason: formData.reason,
                isHalfDay: formData.isHalfDay,
                emergencyContact: formData.emergencyContact
            };

            console.log('✅ Processed leave data before API call:', processedData);
            const response = await requestLeave(processedData);
            console.log('📋 API response:', response);

            if (response.success) {
                await fetchLeaveData(true);
                await fetchAttendanceData();
                setShowLeaveModalFromAbsent(false);
                setSelectedAbsentDate(null);
                alert('Leave request submitted successfully!');
            } else {
                alert(response.message || 'Failed to submit leave request');
            }
        } catch (error) {
            console.error('Error submitting leave request:', error);
            alert('An error occurred while submitting leave request');
        }
    };

    // Handle WFH request success
    const handleWFHRequestSuccess = async () => {
        await fetchAttendanceData();
        setShowWFHModalFromAbsent(false);
        setSelectedAbsentDate(null);
        alert('Work from home request submitted successfully!');
    };

    // NEW: Handle OT/Comp-off request for weekend/holiday work
    const handleOTCompOffRequest = (date) => {
        setSelectedOTDate(date);
        setShowDayModal(false);
        setShowOTCompOffModal(true);
    };

    // Handle conflict resolution request
    const handleConflictResolution = async (resolutionType) => {
        if (!selectedConflict) return;

        setConflictResolutionLoading(true);
        try {
            const requestData = {
                conflictDate: selectedConflict.date,
                resolutionType, // 'cancel_leave' or 'cancel_attendance'
                reason: `Employee requested to ${resolutionType === 'cancel_leave' ? 'cancel leave and mark as present' : 'keep as leave and cancel attendance'} for ${selectedConflict.date}`,
                attendanceId: selectedConflict.attendance?.id,
                leaveRequestId: selectedConflict.attendance?.conflictDetails?.leaveId
            };

            const response = await conflictResolutionService.requestResolution(requestData);

            if (response.success) {
                alert(`✅ Conflict resolution request submitted successfully!\n\nYour ${resolutionType === 'cancel_leave' ? 'leave cancellation' : 'attendance cancellation'} request for ${selectedConflict.date} has been sent to your manager for approval.\n\nThe conflict will be resolved once approved.`);

                // Close modal and refresh data
                setShowConflictModal(false);
                setSelectedConflict(null);
                await fetchAttendanceData();
            } else {
                throw new Error(response.message || 'Failed to submit request');
            }
        } catch (error) {
            console.error('Error submitting conflict resolution request:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Failed to submit conflict resolution request';
            alert(`❌ Error: ${errorMessage}\n\nPlease try again or contact support if the problem persists.`);
        } finally {
            setConflictResolutionLoading(false);
        }
    };

    // Onboarding lock removed - all employees can access attendance
    if (false && !isOnboarded) {
        return (
            <div className="space-y-6">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Attendance</h1>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-500 p-6 rounded-lg">
                    <div className="flex items-start">
                        <Lock className="w-6 h-6 text-yellow-600 dark:text-yellow-400 mt-1" />
                        <div className="ml-4 flex-1">
                            <h2 className="text-lg font-semibold text-yellow-900 dark:text-yellow-200 mb-2">
                                Complete Your Onboarding to Access Attendance
                            </h2>
                            <p className="text-yellow-700 dark:text-yellow-300 mb-4">
                                You need to complete your onboarding process before you can mark attendance.
                            </p>

                            <div className="mb-4">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-yellow-900 dark:text-yellow-200">
                                        Onboarding Progress
                                    </span>
                                    <span className="text-sm font-bold text-yellow-900 dark:text-yellow-200">
                                        {completionPercentage}%
                                    </span>
                                </div>
                                <div className="w-full bg-yellow-200 dark:bg-yellow-800/30 rounded-full h-3">
                                    <div
                                        className="bg-yellow-600 dark:bg-yellow-500 h-3 rounded-full transition-all duration-300"
                                        style={{ width: `${completionPercentage}%` }}
                                    />
                                </div>
                            </div>

                            <button
                                onClick={() => navigate('/employee/onboarding')}
                                className="px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 flex items-center gap-2 font-medium transition-colors"
                            >
                                <UserPlus className="w-5 h-5" />
                                Complete Onboarding Now
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Attendance</h1>

            {/* Check In/Out Card with Active Timer */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Today's Attendance</h2>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(new Date(), { format: 'full' })}
                    </div>
                </div>

                {/* Active Timer Display */}
                {isTimerActive && (
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 mb-6">
                        <div className="flex items-center justify-center">
                            <Activity className="w-6 h-6 text-green-600 dark:text-green-400 mr-3" />
                            <div className="text-center">
                                <p className="text-sm text-gray-600 dark:text-gray-400">Active Session</p>
                                <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                                    {formatTimer(activeTimer)}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Location permission warning */}
                {locationPermission === 'denied' && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg px-4 py-2 mb-4 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
                        <p className="text-xs text-yellow-700 dark:text-yellow-300">
                            Location access is blocked. Enable it in browser settings for attendance verification.
                        </p>
                    </div>
                )}

                {/* NEW: Multiple Punch Buttons */}
                <div className="flex gap-4 mb-6">
                    <button
                        onClick={handleCheckIn}
                        disabled={loading || !canCheckIn}
                        className={`flex-1 px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2
                            ${canCheckIn
                                ? 'bg-green-500 hover:bg-green-600 text-white'
                                : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                            } disabled:opacity-50`}
                    >
                        <LogIn className="w-5 h-5" />
                        {loading ? 'Processing...' : 'Check In'}
                    </button>
                    <button
                        onClick={handleCheckOut}
                        disabled={loading || !canCheckOut}
                        className={`flex-1 px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2
                            ${canCheckOut
                                ? 'bg-red-500 hover:bg-red-600 text-white'
                                : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                            } disabled:opacity-50`}
                    >
                        <LogOut className="w-5 h-5" />
                        {loading ? 'Processing...' : 'Check Out'}
                    </button>
                    <button
                        onClick={() => setShowRegularizationModal(true)}
                        className="px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 whitespace-nowrap text-sm"
                    >
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                        Regularize
                    </button>
                </div>

                {/* NEW: Today's Punch History */}
                {todayPunches.length > 0 && (
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Today's Punch Records</h3>
                        <div className="max-h-[200px] overflow-y-auto space-y-2 pr-2">
                            {todayPunches.map((punch) => {
                                const punchTime = parseApiTimestamp(punch.punch_time);
                                return (
                                    <div key={punch.id} className="flex items-center justify-between text-sm">
                                        <div className="flex items-center gap-2">
                                            {punch.punch_type === 'check_in' ? (
                                                <LogIn className="w-4 h-4 text-green-600 dark:text-green-400" />
                                            ) : (
                                                <LogOut className="w-4 h-4 text-red-600 dark:text-red-400" />
                                            )}
                                            <span className={punch.punch_type === 'check_in' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                                                {punch.punch_type === 'check_in' ? 'Check In' : 'Check Out'}
                                            </span>
                                            {punch.is_manual && (
                                                <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 px-2 py-0.5 rounded">
                                                    Regularized
                                                </span>
                                            )}
                                        </div>
                                        <span className="font-medium text-gray-900 dark:text-white">
                                            {punchTime ? formatTime(punchTime) : '--:--'}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* First Check In */}
                    <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex items-center justify-center mb-2">
                            <LogIn className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" />
                            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">First Check In</span>
                        </div>
                        {firstCheckInMoment ? (
                            <div>
                                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                                    {formatTime(firstCheckInMoment)}
                                </p>
                            </div>
                        ) : (
                            <p className="text-gray-400 dark:text-gray-500 text-lg">--:--</p>
                        )}
                    </div>

                    {/* Last Check Out */}
                    <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex items-center justify-center mb-2">
                            <LogOut className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
                            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Last Check Out</span>
                        </div>
                        {lastCheckOutMoment ? (
                            <div>
                                <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                                    {formatTime(lastCheckOutMoment)}
                                </p>
                            </div>
                        ) : (
                            <p className="text-gray-400 dark:text-gray-500 text-lg">--:--</p>
                        )}
                    </div>

                    {/* Total Hours */}
                    <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex items-center justify-center mb-2">
                            <Timer className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2" />
                            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Hours</span>
                        </div>
                        <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                            {attendanceData?.today?.work_hours || '0.00'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {attendanceData?.today?.overtime_hours > 0 ?
                                `+${attendanceData.today.overtime_hours} OT` :
                                'Regular hours'
                            }
                        </p>
                    </div>
                </div>
            </div>

            {/* Leave Balance Card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                {/*<h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Leave Balance</h2>*/}
                {/*{leaveBalance ? (*/}
                {/*    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">*/}
                {/*        {leaveBalance.map((leave) => (*/}
                {/*            <div key={leave.leave_type} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">*/}
                {/*                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{leave.leave_type}</p>*/}
                {/*                <p className="text-2xl font-bold text-gray-900 dark:text-white">*/}
                {/*                    {leave.available}*/}
                {/*                </p>*/}
                {/*                <p className="text-xs text-gray-500 dark:text-gray-400">*/}
                {/*                    of {leave.total} days*/}
                {/*                </p>*/}
                {/*            </div>*/}
                {/*        ))}*/}
                {/*    </div>*/}
                {/*) : (*/}
                {/*    <p className="text-gray-500 dark:text-gray-400">Loading leave balance...</p>*/}
                {/*)}*/}
                <button
                    onClick={() => navigate('/employee/leaves')}
                    className="mt-4 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                    Apply for Leave →
                </button>
            </div>

            {/* Calendar with Holidays */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Attendance Calendar</h2>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => navigateMonth('prev')}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                            <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        </button>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {formatDate(new Date(selectedYear, selectedMonth), { format: 'long' }).replace(/\d+,?\s*/, '')}
                        </span>
                        <button
                            onClick={() => navigateMonth('next')}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                            <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        </button>
                    </div>
                </div>

                {/* Filter Controls */}
                <div className="flex items-center gap-2 mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg flex-wrap">
                    <Filter className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter:</span>
                    {[
                        { key: 'all', label: 'All', color: 'bg-blue-500' },
                        { key: 'present', label: 'Present', color: 'bg-green-500' },
                        { key: 'absent', label: 'Absent', color: 'bg-red-500' },
                        { key: 'leaves', label: 'Leaves', color: 'bg-indigo-500' },
                        { key: 'wfh', label: 'WFH', color: 'bg-purple-500' },
                        { key: 'office', label: 'Office', color: 'bg-gray-500' },
                        { key: 'early', label: 'Early', color: 'bg-blue-500' },
                        { key: 'ontime', label: 'On-time', color: 'bg-green-500' },
                        { key: 'late', label: 'Late', color: 'bg-red-500' }
                    ].map(filter => (
                        <button
                            key={filter.key}
                            onClick={() => setCalendarFilter(filter.key)}
                            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                                calendarFilter === filter.key
                                    ? `${filter.color} text-white`
                                    : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500'
                            }`}
                        >
                            {filter.label}
                        </button>
                    ))}
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1">
                    {/* Weekday Headers */}
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="text-center py-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                            {day}
                        </div>
                    ))}

                    {/* Calendar Days - Enhanced */}
                    {getCalendarDays().map((date, index) => {
                        const attendance = getAttendanceStatus(date);
                        const holiday = getHolidayInfo(date);
                        const isToday = isSameISTDay(date, new Date());
                        const isCurrentMonth = date.getMonth() === selectedMonth;
                        const isWeekend = isWeeklyOff(date);

                        // Parse times
                        const checkInMoment = attendance?.check_in_time ? parseApiTimestamp(attendance.check_in_time) : null;
                        const checkOutMoment = attendance?.check_out_time ? parseApiTimestamp(attendance.check_out_time) : null;
                        const workHours = attendance?.work_hours ? parseFloat(attendance.work_hours) : 0;
                        const overtimeHours = attendance?.overtime_hours ? parseFloat(attendance.overtime_hours) : 0;

                        // Determine primary status
                        let statusInfo = { label: '', color: '', icon: null, bgColor: '' };
                        if (holiday) {
                            statusInfo = {
                                label: 'Holiday',
                                color: 'text-purple-600 dark:text-purple-400',
                                bgColor: 'bg-purple-50 dark:bg-purple-900/20',
                                icon: <Award className="w-3 h-3" />
                            };
                        } else if (isWeekend) {
                            statusInfo = {
                                label: 'Weekly Off',
                                color: 'text-gray-600 dark:text-gray-400',
                                bgColor: 'bg-gray-50 dark:bg-gray-700/50',
                                icon: <Calendar className="w-3 h-3" />
                            };
                        } else if (attendance?.status === 'present' && !attendance?.check_out_time) {
                            // Present but missing checkout - orange warning
                            statusInfo = {
                                label: 'Missing Checkout',
                                color: 'text-orange-600 dark:text-orange-400',
                                bgColor: 'bg-orange-50 dark:bg-orange-900/20',
                                icon: <AlertTriangle className="w-3 h-3" />
                            };
                        } else if (attendance?.status === 'present') {
                            statusInfo = {
                                label: 'Present',
                                color: 'text-green-600 dark:text-green-400',
                                bgColor: 'bg-green-50 dark:bg-green-900/20',
                                icon: <CheckCircle className="w-3 h-3" />
                            };
                        } else if (attendance?.status === 'absent') {
                            statusInfo = {
                                label: 'Absent',
                                color: 'text-red-600 dark:text-red-400',
                                bgColor: 'bg-red-50 dark:bg-red-900/20',
                                icon: <XCircle className="w-3 h-3" />
                            };
                        } else if (attendance?.status === 'leave') {
                            // Show leave type if available
                            const leaveType = attendance?.leave_type || 'Leave';
                            // Ensure leaveStatus is a string, handle if it's an object
                            let leaveStatus = attendance?.leave_status || attendance?.approval_status;

                            // Convert to string if it's an object
                            if (leaveStatus && typeof leaveStatus === 'object') {
                                // Try to extract string value from object
                                leaveStatus = leaveStatus.status || leaveStatus.type || leaveStatus.value || null;
                                // If still an object, convert to null
                                if (leaveStatus && typeof leaveStatus === 'object') {
                                    leaveStatus = null;
                                }
                            }

                            // Ensure it's a valid string or null
                            const validLeaveStatus = leaveStatus && typeof leaveStatus === 'string' ? String(leaveStatus).trim() : null;

                            statusInfo = {
                                label: leaveType,
                                color: 'text-blue-600 dark:text-blue-400',
                                bgColor: 'bg-blue-50 dark:bg-blue-900/20',
                                icon: <Coffee className="w-3 h-3" />,
                                leaveStatus: validLeaveStatus
                            };
                        } else if (attendance?.status === 'half_day') {
                            statusInfo = {
                                label: 'Half Day',
                                color: 'text-yellow-600 dark:text-yellow-400',
                                bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
                                icon: <Clock className="w-3 h-3" />
                            };
                        } else if (!holiday && !isWeekend && date <= new Date()) {
                            // Mark as absent if it's a working day in the past with no attendance record
                            statusInfo = {
                                label: 'Absent',
                                color: 'text-red-600 dark:text-red-400',
                                bgColor: 'bg-red-50 dark:bg-red-900/20',
                                icon: <XCircle className="w-3 h-3" />
                            };
                        }

                        // Calculate arrival status for filtering
                        const hasShift = !!employeeDetails?.shift_start_time;
                        const isEarly = attendance?.status === 'present' && isEarlyArrival(attendance);
                        const isLate = attendance?.status === 'present' && isLateArrival(attendance);
                        const isOnTime = attendance?.status === 'present' && hasShift && !isEarly && !isLate;
                        const isWfh = attendance?.status === 'present' && attendance?.is_wfh;
                        const isOffice = attendance?.status === 'present' && !attendance?.is_wfh;

                        // Filter logic
                        if (calendarFilter !== 'all') {
                            if (calendarFilter === 'present' && attendance?.status !== 'present') return null;
                            if (calendarFilter === 'absent' && attendance?.status !== 'absent') return null;
                            if (calendarFilter === 'leaves' && attendance?.status !== 'leave') return null;
                            if (calendarFilter === 'wfh' && !isWfh) return null;
                            if (calendarFilter === 'office' && !isOffice) return null;
                            if (calendarFilter === 'early' && !isEarly) return null;
                            if (calendarFilter === 'ontime' && !isOnTime) return null;
                            if (calendarFilter === 'late' && !isLate) return null;
                        }

                        return (
                            <div
                                key={index}
                                onClick={() => {
                                    setSelectedDayDetails({ date, attendance, holiday });
                                    setShowDayModal(true);
                                }}
                                onMouseEnter={() => setHoveredDate(formatLocalDate(date))}
                                onMouseLeave={() => setHoveredDate(null)}
                                className={`
                                    relative p-3 min-h-[120px] border rounded-lg transition-all cursor-pointer group
                                    ${!isCurrentMonth ? 'opacity-30' : ''}
                                    ${isToday ? 'ring-2 ring-blue-500 dark:ring-blue-400 shadow-lg' : ''}
                                    ${statusInfo.bgColor}
                                    hover:shadow-xl hover:scale-[1.02] hover:z-10
                                `}
                            >
                                {/* Header: Date + Location + Arrival Status Badges */}
                                <div className="flex items-start justify-between mb-2">
                                    <div className="flex items-center gap-1 flex-wrap">
                                        <span className={`font-bold text-base ${isToday ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'}`}>
                                            {date.getDate()}
                                        </span>
                                        {isToday && <span className="text-xs bg-blue-500 text-white px-1 rounded">Today</span>}
                                        {/* Location Badge for Present Days */}
                                        {attendance?.status === 'present' && (
                                            attendance?.is_wfh ? (
                                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                                                    WFH
                                                </span>
                                            ) : (
                                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                                    Office
                                                </span>
                                            )
                                        )}
                                        {/* Arrival Status Badge: Early (before shift) | On-time (shift to shift+buffer) | Late (after buffer) */}
                                        {attendance?.status === 'present' && (
                                            isLateArrival(attendance) ? (
                                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                                                    Late
                                                </span>
                                            ) : isEarlyArrival(attendance) ? (
                                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                                    Early
                                                </span>
                                            ) : (
                                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                                                    On-time
                                                </span>
                                            )
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1">
                                        {attendance?.hasConflict && (
                                            <AlertTriangle
                                                className="w-4 h-4 text-yellow-600 dark:text-yellow-400 animate-pulse cursor-pointer"
                                                title="Attendance-Leave Conflict"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedConflict({ date, attendance });
                                                    setShowConflictModal(true);
                                                }}
                                            />
                                        )}
                                        {overtimeHours > 0 && <Zap className="w-3 h-3 text-amber-600 dark:text-amber-400" title="Overtime" />}
                                    </div>
                                </div>

                                {/* Status Label */}
                                {statusInfo.label && (
                                    <div className="space-y-1">
                                        <div className={`flex items-center gap-1 ${statusInfo.color}`}>
                                            {statusInfo.icon}
                                            <span className="text-xs font-semibold">{statusInfo.label}</span>
                                        </div>
                                        {/* Leave Approval Status Badge */}
                                        {statusInfo.leaveStatus && (
                                            <div className="flex items-center gap-1">
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                                    statusInfo.leaveStatus === 'approved' || statusInfo.leaveStatus === 'Approved'
                                                        ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                                                        : statusInfo.leaveStatus === 'rejected' || statusInfo.leaveStatus === 'Rejected'
                                                        ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                                                        : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300'
                                                }`}>
                                                    {statusInfo.leaveStatus === 'approved' || statusInfo.leaveStatus === 'Approved' ? '✓' :
                                                     statusInfo.leaveStatus === 'rejected' || statusInfo.leaveStatus === 'Rejected' ? '✗' : '⏳'} {statusInfo.leaveStatus}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Holiday Name */}
                                {holiday && (
                                    <div className="text-xs text-purple-700 dark:text-purple-300 font-medium mb-1 truncate">
                                        {holiday.name}
                                    </div>
                                )}

                                {/* Attendance Details for Present Days */}
                                {attendance?.status === 'present' && (
                                    <div className="space-y-1">
                                        {/* Time Range */}
                                        {checkInMoment && (
                                            <div className="flex items-center gap-1 text-xs text-gray-700 dark:text-gray-300">
                                                <Clock className="w-3 h-3" />
                                                <span>
                                                    {formatTime(checkInMoment, { hour12: false })}
                                                    {checkOutMoment && (
                                                        <> → {formatTime(checkOutMoment, { hour12: false })}</>
                                                    )}
                                                </span>
                                            </div>
                                        )}

                                        {/* Work Hours */}
                                        {workHours > 0 && (
                                            <div className="flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400">
                                                <Timer className="w-3 h-3" />
                                                <span>{workHours.toFixed(1)}h</span>
                                                {overtimeHours > 0 && (
                                                    <span className="text-amber-600 dark:text-amber-400">+{overtimeHours.toFixed(1)}</span>
                                                )}
                                            </div>
                                        )}

                                        {/* Multiple Punches Indicator */}
                                        {attendance?.punch_count > 2 && (
                                            <div className="text-xs text-gray-600 dark:text-gray-400">
                                                📍 {attendance.punch_count} punches
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Hover Tooltip */}
                                {hoveredDate === formatLocalDate(date) && attendance && (
                                    <div className="absolute left-0 top-full mt-2 w-64 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-xl p-3 z-50 hidden group-hover:block">
                                        <div className="font-semibold mb-2">
                                            {formatDate(date, { format: 'full' })}
                                        </div>
                                        <div className="space-y-1">
                                            <div>Status: <span className="font-semibold">{statusInfo.label}</span></div>
                                            {checkInMoment && <div>Check-in: {formatTime(checkInMoment)}</div>}
                                            {checkOutMoment && <div>Check-out: {formatTime(checkOutMoment)}</div>}
                                            {workHours > 0 && <div>Hours: {workHours.toFixed(2)}</div>}
                                            {overtimeHours > 0 && <div>Overtime: +{overtimeHours.toFixed(2)}h</div>}
                                            {attendance?.location && <div>Location: {typeof attendance.location === 'string' ? attendance.location : JSON.stringify(attendance.location)}</div>}
                                        </div>
                                        <div className="mt-2 text-gray-400 text-[10px]">Click for details →</div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Legend */}
                <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs">
                    {/* Status */}
                    <div className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded bg-green-100 dark:bg-green-900/30"></div>
                        <span className="text-gray-600 dark:text-gray-400">Present</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded bg-red-100 dark:bg-red-900/30"></div>
                        <span className="text-gray-600 dark:text-gray-400">Absent</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded bg-blue-100 dark:bg-blue-900/30"></div>
                        <span className="text-gray-600 dark:text-gray-400">Leave</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded bg-purple-100 dark:bg-purple-900/30"></div>
                        <span className="text-gray-600 dark:text-gray-400">Holiday</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded bg-gray-100 dark:bg-gray-700"></div>
                        <span className="text-gray-600 dark:text-gray-400">Weekly Off {employeeDetails?.weekly_off ? `(${employeeDetails.weekly_off})` : ''}</span>
                    </div>
                    {/* Location */}
                    <span className="text-gray-400 dark:text-gray-500">|</span>
                    <div className="flex items-center gap-1">
                        <span className="px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-[9px]">WFH</span>
                        <span className="text-gray-600 dark:text-gray-400">Work From Home</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-[9px]">Office</span>
                        <span className="text-gray-600 dark:text-gray-400">Office</span>
                    </div>
                    {/* Arrival */}
                    <span className="text-gray-400 dark:text-gray-500">|</span>
                    <div className="flex items-center gap-1">
                        <span className="px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-[9px]">Early</span>
                        <span className="text-gray-600 dark:text-gray-400">Before shift</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <span className="px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-[9px]">On-time</span>
                        <span className="text-gray-600 dark:text-gray-400">Within grace</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <span className="px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-[9px]">Late</span>
                        <span className="text-gray-600 dark:text-gray-400">After grace</span>
                    </div>
                </div>
            </div>

            {/* Monthly Statistics */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Monthly Statistics</h2>
                {attendanceData?.stats && (
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                        <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <Users className="w-6 h-6 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                {attendanceData.stats.totalDays}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Total working days</p>
                        </div>
                        <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                            <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 mx-auto mb-2" />
                            <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                                {attendanceData.stats.presentDays}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Present</p>
                        </div>
                        <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                            <XCircle className="w-6 h-6 text-red-600 dark:text-red-400 mx-auto mb-2" />
                            <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                                {attendanceData.stats.absentDays}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Absent</p>
                        </div>
                        <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                            <Coffee className="w-6 h-6 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
                            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                {attendanceData.stats.leaveDays}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Leaves</p>
                        </div>
                        <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                            <Timer className="w-6 h-6 text-purple-600 dark:text-purple-400 mx-auto mb-2" />
                            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                                {attendanceData.stats.averageWorkHours?.toFixed(1)}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Avg Hours</p>
                        </div>
                        <div className="text-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                            <TrendingUp className="w-6 h-6 text-orange-600 dark:text-orange-400 mx-auto mb-2" />
                            <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                                {attendanceData.stats.totalOvertime?.toFixed(1)}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Overtime</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Attendance History Table - Commented Out
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Attendance History</h2>
                </div>
                <div className="max-h-[500px] overflow-x-auto overflow-y-auto">
                    <table className="min-w-full">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Day
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Check In
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Check Out
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Hours
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Status
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {attendanceData?.monthly?.slice(0, 10).map((record) => {
                                const checkInMoment = parseApiTimestamp(record.check_in_time);
                                const checkOutMoment = parseApiTimestamp(record.check_out_time);

                                return (
                                    <tr key={record.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                                        {formatDate(getISTDateFromUTC(record.date), { format: 'full' }).split(',')[0]}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                                        {formatDate(getISTDateFromUTC(record.date), { format: 'medium' })}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                                        {checkInMoment ? formatTime(checkInMoment) : '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                                        {checkOutMoment ? formatTime(checkOutMoment) : '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                                        {record.work_hours || '0'} hrs
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                            record.status === 'present' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                                            record.status === 'absent' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
                                            record.status === 'leave' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                                            'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                        }`}>
                                            {record.status}
                                        </span>
                                    </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
            */}

            {/* NEW: Regularization Request History */}
            {regularizationRequests.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Regularization Requests</h2>
                    <div className="max-h-[400px] overflow-y-auto space-y-3 pr-2">
                        {regularizationRequests.map((request) => {
                            const attendanceMoment = parseApiTimestamp(request.attendance_date);
                            const requestedInMoment = parseApiTimestamp(request.requested_punch_in);
                            const requestedOutMoment = parseApiTimestamp(request.requested_punch_out);

                            return (
                                <div key={request.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                    <div className="flex items-start justify-between mb-2">
                                        <div>
                                            <p className="font-medium text-gray-900 dark:text-white">
                                                {attendanceMoment
                                                    ? formatDate(attendanceMoment, { format: 'full' })
                                                    : request.attendance_date}
                                            </p>
                                            <p className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                                                Type: {request.regularization_type?.replace('_', ' ')}
                                            </p>
                                        </div>
                                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                            request.status === 'approved' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                                            request.status === 'rejected' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
                                            'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                        }`}>
                                            {request.status}
                                        </span>
                                    </div>
                                    <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                                        {requestedInMoment && (
                                            <p>
                                                Check In: {formatTime(requestedInMoment)}
                                            </p>
                                        )}
                                        {requestedOutMoment && (
                                            <p>
                                                Check Out: {formatTime(requestedOutMoment)}
                                            </p>
                                        )}
                                        <p className="text-gray-600 dark:text-gray-400 italic">Reason: {request.reason}</p>
                                        {request.manager_comments && (
                                            <p className="text-sm text-blue-600 dark:text-blue-400 mt-2">
                                                Manager: {request.manager_comments}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <AttendanceRegularizationModal
                isOpen={showRegularizationModal}
                onClose={() => setShowRegularizationModal(false)}
                managerId={
                    employeeDetails?.reporting_manager_id ||
                    employeeDetails?.managerId ||
                    currentEmployee?.reporting_manager_id ||
                    currentEmployee?.managerId ||
                    user?.reporting_manager_id ||
                    user?.managerId
                }
                onSubmitSuccess={async () => {
                    await fetchRegularizationRequests();
                    await fetchAttendanceData();
                }}
                shiftStartTime={employeeDetails?.shift_start_time || currentEmployee?.shift_start_time}
                shiftEndTime={employeeDetails?.shift_end_time || currentEmployee?.shift_end_time}
            />

            {/* Day Details Modal */}
            {showDayModal && selectedDayDetails && (
                <>
                    <div
                        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-40"
                        onClick={() => setShowDayModal(false)}
                        aria-hidden="true"
                    ></div>
                    <div className="fixed inset-0 z-50 overflow-y-auto pointer-events-none">
                        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                            <div
                                className="pointer-events-auto inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full"
                                onClick={(e) => e.stopPropagation()}
                            >
                            <div className="bg-white dark:bg-gray-800 px-6 pt-5 pb-4">
                                {/* Modal Header */}
                                <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                                        {formatDate(selectedDayDetails.date, { format: 'full' })}
                                    </h3>
                                    <button
                                        onClick={() => setShowDayModal(false)}
                                        className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                                    >
                                        <X className="w-6 h-6" />
                                    </button>
                                </div>

                                {/* Modal Content */}
                                <div className="space-y-4">
                                    {/* Holiday Info */}
                                    {selectedDayDetails.holiday && (
                                        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                                            <div className="flex items-center gap-2 text-purple-800 dark:text-purple-200 mb-2">
                                                <Award className="w-5 h-5" />
                                                <span className="font-semibold">Holiday</span>
                                            </div>
                                            <p className="text-purple-700 dark:text-purple-300 font-medium">
                                                {selectedDayDetails.holiday.name}
                                            </p>
                                            {selectedDayDetails.holiday.description && (
                                                <p className="text-sm text-purple-600 dark:text-purple-400 mt-1">
                                                    {selectedDayDetails.holiday.description}
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    {/* Attendance Info */}
                                    {selectedDayDetails.attendance ? (
                                        <div className="space-y-3">
                                            {/* Status */}
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Status:</span>
                                                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                                                    selectedDayDetails.attendance.status === 'present'
                                                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                                        : selectedDayDetails.attendance.status === 'absent'
                                                        ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                                        : selectedDayDetails.attendance.status === 'leave'
                                                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                                                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                                }`}>
                                                    {selectedDayDetails.attendance.status.toUpperCase()}
                                                </span>
                                            </div>

                                            {/* Time Details */}
                                            {selectedDayDetails.attendance.check_in_time && (
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                                                        <div className="flex items-center gap-2 text-green-700 dark:text-green-300 mb-1">
                                                            <LogIn className="w-4 h-4" />
                                                            <span className="text-xs font-medium">Check In</span>
                                                        </div>
                                                        <p className="text-lg font-bold text-green-900 dark:text-green-100">
                                                            {formatTime(parseApiTimestamp(selectedDayDetails.attendance.check_in_time))}
                                                        </p>
                                                    </div>
                                                    {selectedDayDetails.attendance.check_out_time && (
                                                        <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                                                            <div className="flex items-center gap-2 text-red-700 dark:text-red-300 mb-1">
                                                                <LogOut className="w-4 h-4" />
                                                                <span className="text-xs font-medium">Check Out</span>
                                                            </div>
                                                            <p className="text-lg font-bold text-red-900 dark:text-red-100">
                                                                {formatTime(parseApiTimestamp(selectedDayDetails.attendance.check_out_time))}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Arrival Status - Early/On-time/Late with duration */}
                                            {selectedDayDetails.attendance.status === 'present' && selectedDayDetails.attendance.check_in_time && employeeDetails?.shift_start_time && (() => {
                                                const checkInTime = parseApiTimestamp(selectedDayDetails.attendance.check_in_time);
                                                if (!checkInTime) return null;

                                                const [shiftHour, shiftMin] = employeeDetails.shift_start_time.split(':').map(Number);
                                                const gracePeriod = employeeDetails?.shift_grace_period || 15;
                                                const { hour: checkInHour, minute: checkInMin } = getISTComponents(checkInTime);

                                                const checkInMinutes = checkInHour * 60 + checkInMin;
                                                const shiftMinutes = shiftHour * 60 + shiftMin;
                                                const allowedLateMinutes = shiftMinutes + gracePeriod;

                                                const diffMinutes = checkInMinutes - shiftMinutes;
                                                const absDiff = Math.abs(diffMinutes);
                                                const hours = Math.floor(absDiff / 60);
                                                const minutes = absDiff % 60;

                                                let durationText = '';
                                                if (hours > 0 && minutes > 0) {
                                                    durationText = `${hours}h ${minutes}m`;
                                                } else if (hours > 0) {
                                                    durationText = `${hours} hour${hours > 1 ? 's' : ''}`;
                                                } else {
                                                    durationText = `${minutes} minute${minutes !== 1 ? 's' : ''}`;
                                                }

                                                const shiftTimeFormatted = `${shiftHour > 12 ? shiftHour - 12 : shiftHour || 12}:${String(shiftMin).padStart(2, '0')} ${shiftHour >= 12 ? 'PM' : 'AM'}`;

                                                if (checkInMinutes < shiftMinutes) {
                                                    // Early
                                                    return (
                                                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-3 rounded-lg">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center gap-2">
                                                                    <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                                                    <span className="font-medium text-blue-800 dark:text-blue-200">Early Arrival</span>
                                                                </div>
                                                                <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded text-sm font-semibold">
                                                                    {durationText} early
                                                                </span>
                                                            </div>
                                                            <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                                                                Shift starts at {shiftTimeFormatted}
                                                            </p>
                                                        </div>
                                                    );
                                                } else if (checkInMinutes > allowedLateMinutes) {
                                                    // Late
                                                    const lateBy = checkInMinutes - shiftMinutes;
                                                    const lateHours = Math.floor(lateBy / 60);
                                                    const lateMinutes = lateBy % 60;
                                                    let lateDurationText = '';
                                                    if (lateHours > 0 && lateMinutes > 0) {
                                                        lateDurationText = `${lateHours}h ${lateMinutes}m`;
                                                    } else if (lateHours > 0) {
                                                        lateDurationText = `${lateHours} hour${lateHours > 1 ? 's' : ''}`;
                                                    } else {
                                                        lateDurationText = `${lateMinutes} minute${lateMinutes !== 1 ? 's' : ''}`;
                                                    }

                                                    return (
                                                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 rounded-lg">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center gap-2">
                                                                    <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                                                                    <span className="font-medium text-red-800 dark:text-red-200">Late Arrival</span>
                                                                </div>
                                                                <span className="px-2 py-1 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded text-sm font-semibold">
                                                                    {lateDurationText} late
                                                                </span>
                                                            </div>
                                                            <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                                                                Shift starts at {shiftTimeFormatted} (Grace: {gracePeriod} min)
                                                            </p>
                                                        </div>
                                                    );
                                                } else {
                                                    // On-time
                                                    return (
                                                        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-3 rounded-lg">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center gap-2">
                                                                    <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                                                                    <span className="font-medium text-green-800 dark:text-green-200">On-time Arrival</span>
                                                                </div>
                                                                <span className="px-2 py-1 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded text-sm font-semibold">
                                                                    Within grace period
                                                                </span>
                                                            </div>
                                                            <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                                                                Shift starts at {shiftTimeFormatted} (Grace: {gracePeriod} min)
                                                            </p>
                                                        </div>
                                                    );
                                                }
                                            })()}

                                            {/* Work Hours */}
                                            {selectedDayDetails.attendance.work_hours && (
                                                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <Timer className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                                            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Work Hours</span>
                                                        </div>
                                                        <span className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                                                            {parseFloat(selectedDayDetails.attendance.work_hours).toFixed(2)}h
                                                        </span>
                                                    </div>
                                                    {selectedDayDetails.attendance.overtime_hours > 0 && (
                                                        <div className="mt-2 flex items-center justify-between text-sm">
                                                            <span className="text-amber-700 dark:text-amber-300 flex items-center gap-1">
                                                                <Zap className="w-4 h-4" />
                                                                Overtime
                                                            </span>
                                                            <span className="font-semibold text-amber-900 dark:text-amber-100">
                                                                +{parseFloat(selectedDayDetails.attendance.overtime_hours).toFixed(2)}h
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Additional Info */}
                                            <div className="space-y-2 text-sm">
                                                {selectedDayDetails.attendance.is_wfh && (
                                                    <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400">
                                                        <Home className="w-4 h-4" />
                                                        <span>Work From Home</span>
                                                    </div>
                                                )}
                                                {isLateArrival(selectedDayDetails.attendance) && (
                                                    <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                                                        <AlertTriangle className="w-4 h-4" />
                                                        <span>Late Arrival</span>
                                                    </div>
                                                )}
                                                {selectedDayDetails.attendance.location && (
                                                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                                        <MapPin className="w-4 h-4" />
                                                        <span>Location: {typeof selectedDayDetails.attendance.location === 'string' ? selectedDayDetails.attendance.location : JSON.stringify(selectedDayDetails.attendance.location)}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                            <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                            <p>No attendance record for this day</p>
                                        </div>
                                    )}

                                    {/* Action Buttons for Absent Days */}
                                    {(() => {
                                        const date = selectedDayDetails.date;
                                        const attendance = selectedDayDetails.attendance;
                                        const holiday = selectedDayDetails.holiday;
                                        const isWeekend = isWeeklyOff(date);

                                        // Show actions if:
                                        // 1. Status is explicitly 'absent', OR
                                        // 2. No attendance record exists for a past working day (not holiday, not weekend)
                                        const isAbsent = attendance?.status === 'absent' ||
                                                        (!attendance && !holiday && !isWeekend && date <= new Date());

                                        return isAbsent;
                                    })() && (
                                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                                Quick Actions for Absent Day
                                            </h4>
                                            <div className="grid grid-cols-1 gap-3">
                                                <button
                                                    onClick={() => handleAbsentDayLeaveRequest(selectedDayDetails.date)}
                                                    className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
                                                >
                                                    <Calendar className="w-5 h-5" />
                                                    Apply Leave
                                                </button>
                                                <button
                                                    onClick={() => handleAbsentDayWFHRequest(selectedDayDetails.date)}
                                                    className="flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium"
                                                >
                                                    <Home className="w-5 h-5" />
                                                    Apply WFH Request
                                                </button>
                                                <button
                                                    onClick={() => handleAbsentDayRegularization(selectedDayDetails.date)}
                                                    className="flex items-center justify-center gap-2 px-4 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors font-medium"
                                                >
                                                    <AlertCircle className="w-5 h-5" />
                                                    Apply Regularization
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Action Button for Missing Checkout */}
                                    {selectedDayDetails.attendance?.status === 'present' &&
                                     selectedDayDetails.attendance?.check_in_time &&
                                     !selectedDayDetails.attendance?.check_out_time && (
                                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                                            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4 mb-3">
                                                <div className="flex items-center gap-2 text-orange-700 dark:text-orange-300 mb-2">
                                                    <AlertTriangle className="w-5 h-5" />
                                                    <span className="font-semibold">Missing Checkout</span>
                                                </div>
                                                <p className="text-sm text-orange-600 dark:text-orange-400">
                                                    No checkout recorded for this day. Submit a regularization request to add your checkout time.
                                                </p>
                                            </div>
                                            <button
                                                onClick={() => handleAbsentDayRegularization(selectedDayDetails.date)}
                                                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors font-medium"
                                            >
                                                <Clock className="w-5 h-5" />
                                                Add Regularization Request
                                            </button>
                                        </div>
                                    )}

                                    {/* Action Button for Weekend/Holiday Work (OT & Comp-off) */}
                                    {(() => {
                                        const date = selectedDayDetails.date;
                                        const attendance = selectedDayDetails.attendance;
                                        const holiday = selectedDayDetails.holiday;
                                        const isWeekend = isWeeklyOff(date);

                                        // Show OT/Comp-off button if:
                                        // - It's a weekend OR holiday
                                        // - AND there is attendance record (employee worked on off day)
                                        const workedOnOffDay = (isWeekend || holiday) && attendance && attendance.check_in_time;

                                        return workedOnOffDay;
                                    })() && (
                                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                                Worked on {selectedDayDetails.holiday ? 'Holiday' : 'Weekly Off'}
                                            </h4>
                                            <button
                                                onClick={() => handleOTCompOffRequest(selectedDayDetails.date)}
                                                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors font-medium"
                                            >
                                                <Zap className="w-5 h-5" />
                                                Request for OT
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Modal Footer */}
                                <div className="mt-5 pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                                    <button
                                        onClick={() => setShowDayModal(false)}
                                        className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    </div>
                </>
            )}

            {/* Leave Request Modal - Triggered from Absent Day */}
            {showLeaveModalFromAbsent && selectedAbsentDate && (
                <LeaveRequestModal
                    leaveTypes={rawLeaveBalance?.balance || rawLeaveBalance?.balances || []}
                    onClose={() => {
                        setShowLeaveModalFromAbsent(false);
                        setSelectedAbsentDate(null);
                    }}
                    onSubmit={handleLeaveRequestSubmit}
                    prefilledFromDate={formatLocalDate(selectedAbsentDate)}
                    prefilledToDate={formatLocalDate(selectedAbsentDate)}
                />
            )}

            {/* Work From Home Modal - Triggered from Absent Day */}
            {showWFHModalFromAbsent && selectedAbsentDate && (
                <WorkFromHomeModal
                    isOpen={showWFHModalFromAbsent}
                    onClose={() => {
                        setShowWFHModalFromAbsent(false);
                        setSelectedAbsentDate(null);
                    }}
                    managerId={
                        employeeDetails?.reporting_manager_id ||
                        employeeDetails?.managerId ||
                        currentEmployee?.reporting_manager_id ||
                        currentEmployee?.managerId ||
                        user?.reporting_manager_id ||
                        user?.managerId
                    }
                    onSubmitSuccess={handleWFHRequestSuccess}
                    prefilledFromDate={formatLocalDate(selectedAbsentDate)}
                    prefilledToDate={formatLocalDate(selectedAbsentDate)}
                />
            )}

            {/* Attendance Regularization Modal - Triggered from Absent Day */}
            {showRegularizationModalFromAbsent && selectedAbsentDate && (
                <AttendanceRegularizationModal
                    isOpen={showRegularizationModalFromAbsent}
                    onClose={() => {
                        setShowRegularizationModalFromAbsent(false);
                        setSelectedAbsentDate(null);
                    }}
                    managerId={
                        employeeDetails?.reporting_manager_id ||
                        employeeDetails?.managerId ||
                        currentEmployee?.reporting_manager_id ||
                        currentEmployee?.managerId ||
                        user?.reporting_manager_id ||
                        user?.managerId
                    }
                    onSubmitSuccess={async () => {
                        await fetchRegularizationRequests();
                        await fetchAttendanceData();
                        setShowRegularizationModalFromAbsent(false);
                        setSelectedAbsentDate(null);
                    }}
                    initialDate={formatLocalDate(selectedAbsentDate)}
                    shiftStartTime={employeeDetails?.shift_start_time || currentEmployee?.shift_start_time}
                    shiftEndTime={employeeDetails?.shift_end_time || currentEmployee?.shift_end_time}
                />
            )}

            {/* Attendance Regularization Modal - OT/Comp-off for Weekend/Holiday Work */}
            {showOTCompOffModal && selectedOTDate && (
                <AttendanceRegularizationModal
                    isOpen={showOTCompOffModal}
                    onClose={() => {
                        setShowOTCompOffModal(false);
                        setSelectedOTDate(null);
                    }}
                    managerId={
                        employeeDetails?.reporting_manager_id ||
                        employeeDetails?.managerId ||
                        currentEmployee?.reporting_manager_id ||
                        currentEmployee?.managerId ||
                        user?.reporting_manager_id ||
                        user?.managerId
                    }
                    onSubmitSuccess={async () => {
                        await fetchRegularizationRequests();
                        await fetchAttendanceData();
                        setShowOTCompOffModal(false);
                        setSelectedOTDate(null);
                        alert('OT and Comp-Off request submitted successfully!');
                    }}
                    initialDate={formatLocalDate(selectedOTDate)}
                    initialType="overtime_comp_off"
                    shiftStartTime={employeeDetails?.shift_start_time || currentEmployee?.shift_start_time}
                    shiftEndTime={employeeDetails?.shift_end_time || currentEmployee?.shift_end_time}
                />
            )}

            {/* Conflict Resolution Modal */}
            {showConflictModal && selectedConflict && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        {/* Header */}
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800 px-6 py-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <AlertTriangle className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                        Attendance-Leave Conflict Detected
                                    </h2>
                                </div>
                                <button
                                    onClick={() => {
                                        setShowConflictModal(false);
                                        setSelectedConflict(null);
                                    }}
                                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-6 space-y-6">
                            {/* Warning Message */}
                            <div className="bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 rounded-lg p-4">
                                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                                    <strong>Conflict Date:</strong> {formatDate(selectedConflict.date, { format: 'full' })}
                                </p>
                                <p className="text-sm text-yellow-800 dark:text-yellow-200 mt-2">
                                    This date has both <strong>approved leave</strong> and <strong>attendance records</strong>.
                                    Please resolve this conflict by choosing one of the options below.
                                </p>
                            </div>

                            {/* Conflict Details */}
                            <div className="grid md:grid-cols-2 gap-4">
                                {/* Leave Details */}
                                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                                    <h3 className="font-semibold text-blue-900 dark:text-blue-100 flex items-center gap-2 mb-3">
                                        <Coffee className="w-4 h-4" />
                                        Approved Leave
                                    </h3>
                                    <div className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
                                        <p><strong>Leave Type:</strong> {selectedConflict.attendance.conflictDetails?.leaveType || 'N/A'}</p>
                                        <p><strong>From:</strong> {selectedConflict.attendance.conflictDetails?.fromDate ? formatDate(selectedConflict.attendance.conflictDetails.fromDate) : 'N/A'}</p>
                                        <p><strong>To:</strong> {selectedConflict.attendance.conflictDetails?.toDate ? formatDate(selectedConflict.attendance.conflictDetails.toDate) : 'N/A'}</p>
                                    </div>
                                </div>

                                {/* Attendance Details */}
                                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                                    <h3 className="font-semibold text-green-900 dark:text-green-100 flex items-center gap-2 mb-3">
                                        <CheckCircle className="w-4 h-4" />
                                        Attendance Record
                                    </h3>
                                    <div className="space-y-2 text-sm text-green-800 dark:text-green-200">
                                        <p><strong>Check In:</strong> {selectedConflict.attendance.check_in || 'N/A'}</p>
                                        <p><strong>Check Out:</strong> {selectedConflict.attendance.check_out || 'N/A'}</p>
                                        <p><strong>Work Hours:</strong> {selectedConflict.attendance.work_hours ? `${selectedConflict.attendance.work_hours} hrs` : 'N/A'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Current Status */}
                            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                                <p className="text-sm text-gray-700 dark:text-gray-300">
                                    <strong>Current Status:</strong> This date is currently marked as <strong className="text-blue-600 dark:text-blue-400">Leave</strong>
                                    until you get approval from your reporting manager or admin to resolve this conflict.
                                </p>
                            </div>

                            {/* Resolution Options */}
                            <div className="space-y-3">
                                <h3 className="font-semibold text-gray-900 dark:text-white">Resolution Options:</h3>

                                {/* Option 1: Cancel Leave */}
                                <button
                                    onClick={() => handleConflictResolution('cancel_leave')}
                                    disabled={conflictResolutionLoading}
                                    className="w-full bg-green-50 dark:bg-green-900/20 border-2 border-green-300 dark:border-green-700 rounded-lg p-4 text-left hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <div className="flex items-start gap-3">
                                        <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" />
                                        <div className="flex-1">
                                            <p className="font-medium text-gray-900 dark:text-white mb-1">
                                                Request to Cancel Leave & Mark as Present
                                            </p>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                Cancel the approved leave and confirm attendance for this day.
                                                Requires manager/admin approval.
                                            </p>
                                        </div>
                                    </div>
                                </button>

                                {/* Option 2: Keep as Leave */}
                                <button
                                    onClick={() => handleConflictResolution('cancel_attendance')}
                                    disabled={conflictResolutionLoading}
                                    className="w-full bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-300 dark:border-blue-700 rounded-lg p-4 text-left hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <div className="flex items-start gap-3">
                                        <Coffee className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                                        <div className="flex-1">
                                            <p className="font-medium text-gray-900 dark:text-white mb-1">
                                                Keep as Leave Day
                                            </p>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                Continue with the approved leave and ignore the attendance record.
                                                Requires manager/admin approval.
                                            </p>
                                        </div>
                                    </div>
                                </button>
                            </div>

                            {/* Info */}
                            <div className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                                <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                <p>
                                    The conflict will remain until your reporting manager or admin approves your resolution request.
                                    The warning icon will continue to appear on the calendar.
                                </p>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 bg-gray-50 dark:bg-gray-900/50">
                            <button
                                onClick={() => {
                                    setShowConflictModal(false);
                                    setSelectedConflict(null);
                                }}
                                disabled={conflictResolutionLoading}
                                className="w-full sm:w-auto px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {conflictResolutionLoading ? 'Processing...' : 'Close'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeeAttendance;
